import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConfirm } from '@hooks/useConfirm';
import { USER_MENU_ITEMS } from '@constants/navigation';
import { useAppDispatch, useAppSelector } from '@store/index';
import { logout, selectUser } from '@features/auth/authSlice';

const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const user = useAppSelector(selectUser);

  const handleLogout = async () => {
    if (
      !(await confirm({
        title: t('navigation.logoutConfirm.title'),
        description: t('navigation.logoutConfirm.description'),
        confirmLabel: t('navigation.logoutConfirm.confirm'),
      }))
    ) {
      return;
    }
    await dispatch(logout());
    navigate('/login');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('navigation.profile')}
          className="rounded-full"
        >
          <Avatar className="size-7">
            <AvatarFallback className="bg-primary text-[11px] font-semibold text-primary-foreground">
              {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="revy-ui w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate text-sm font-medium">
            {user?.fullName || t('navigation.profile')}
          </span>
          {user?.email && (
            <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {USER_MENU_ITEMS.map(({ path, labelKey, icon: Icon }) => (
          <DropdownMenuItem key={path} onSelect={() => navigate(path)}>
            <Icon className="mr-2 size-4" strokeWidth={1.75} />
            {t(labelKey)}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          // preventDefault stops Radix returning focus to the trigger while the
          // confirmation is mounting and claiming it in the same tick.
          onSelect={(event) => {
            event.preventDefault();
            handleLogout();
          }}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 size-4" strokeWidth={1.75} />
          {t('navigation.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
